import React, { useState, useEffect } from 'react';
import { 
  FaCogs,
  FaUserShield,
  FaCrown,
  FaRocket,
  FaEdit,
  FaInfinity
} from 'react-icons/fa';
import PlanLimitsEditModal from '../../components/PlanLimitsEditModal';

const subscriptionPlans = [
  { value: 'free', label: 'Ücretsiz', color: 'bg-gray-100 text-gray-800', icon: FaUserShield },
  { value: 'premium', label: 'Premium', color: 'bg-blue-100 text-blue-800', icon: FaCrown },
  { value: 'enterprise', label: 'Kurumsal', color: 'bg-purple-100 text-purple-800', icon: FaRocket }
];

const AdminPlanLimits = () => {
  const [planLimits, setPlanLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlanLimitsModal, setShowPlanLimitsModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isUpdatingPlanLimits, setIsUpdatingPlanLimits] = useState(false);

  const fetchPlanLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const planLimitsRes = await fetch('/api/auth/admin/plan-limits', { headers });
      if (planLimitsRes.ok) {
        const planLimitsData = await planLimitsRes.json();
        setPlanLimits(planLimitsData.planLimits || []);
      }
    } catch (error) {
      console.error('Error fetching plan limits:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanLimits();
  }, []);

  const handleUpdatePlanLimits = async (plan, updatedLimits) => {
    setIsUpdatingPlanLimits(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/auth/admin/plan-limits/${plan}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedLimits)
      });
      
      if (response.ok) {
        await fetchPlanLimits(); // Refresh data
        setShowPlanLimitsModal(false);
        setSelectedPlan(null);
        alert('Plan limitleri başarıyla güncellendi!');
      } else {
        const errorData = await response.json();
        alert(`Güncelleme hatası: ${errorData.message}`);
      }
    } catch (error) {
      alert('Plan limitleri güncellenirken bir hata oluştu.');
    } finally {
      setIsUpdatingPlanLimits(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-6">Plan Limitleri</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {planLimits.map((planLimit) => {
              const planInfo = subscriptionPlans.find(p => p.value === planLimit.plan);
              const PlanIcon = planInfo?.icon || FaCogs;
              
              return (
                <div key={planLimit.plan} className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:bg-gray-750 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        planLimit.plan === 'free' ? 'bg-gray-600/20' :
                        planLimit.plan === 'premium' ? 'bg-blue-600/20' :
                        'bg-purple-600/20'
                      }`}>
                        <PlanIcon className={`h-6 w-6 ${
                          planLimit.plan === 'free' ? 'text-gray-400' :
                          planLimit.plan === 'premium' ? 'text-blue-400' :
                          'text-purple-400'
                        }`} />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-lg font-semibold text-white">{planInfo?.label || planLimit.plan}</h4>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mt-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Projeler</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxProjects === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxProjects}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Dökümanlar</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxDocuments === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxDocuments}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Ürünler/Proje</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxProductsPerProject === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxProductsPerProject}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Modüller/Proje</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxModulesPerProject === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxModulesPerProject}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Kısa Linkler</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxShortLinks === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxShortLinks}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Destek Modülleri</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxSupportModules === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxSupportModules}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Internal Ads</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxInternalAds === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxInternalAds}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Landing Pages</span>
                      <span className="text-sm font-medium text-white">
                        {planLimit.limits.maxLandingPages === -1 ? <FaInfinity className="inline h-4 w-4" /> : planLimit.limits.maxLandingPages}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-sm text-gray-400">Aylık Fiyat</span>
                      <span className="text-sm font-medium text-white">
                        ${planLimit.pricing.monthly}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-400">Yıllık Fiyat</span>
                      <span className="text-sm font-medium text-white">
                        ${planLimit.pricing.yearly}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setSelectedPlan(planLimit);
                      setShowPlanLimitsModal(true);
                    }}
                    className="mt-6 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <FaEdit className="mr-2 h-4 w-4" />
                    Düzenle
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Plan Limits Edit Modal */}
      {showPlanLimitsModal && selectedPlan && (
        <PlanLimitsEditModal
          plan={selectedPlan}
          onSave={handleUpdatePlanLimits}
          onCancel={() => {
            setShowPlanLimitsModal(false);
            setSelectedPlan(null);
          }}
          isUpdating={isUpdatingPlanLimits}
        />
      )}
    </div>
  );
};

export default AdminPlanLimits;
